/** 功能：兼容安全上下文、HTTP 与内嵌浏览器的文本复制。 */
export async function writeClipboardText(value: string): Promise<void> {
  const text = String(value ?? '');
  if (!text) throw new Error('没有可复制的内容');

  let clipboardError: unknown = null;
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (error) {
      clipboardError = error;
    }
  }

  try {
    copyWithSelection(text);
  } catch (fallbackError) {
    const detail = fallbackError instanceof Error
      ? fallbackError.message
      : clipboardError instanceof Error
        ? clipboardError.message
        : '';
    throw new Error(detail ? `浏览器未允许复制：${detail}` : '浏览器未允许复制');
  }
}

/** 功能：使用隐藏文本域和浏览器选区完成 Clipboard API 的兼容回退。 */
function copyWithSelection(text: string): void {
  if (!document.body || typeof document.execCommand !== 'function') throw new Error('当前页面不支持剪贴板回退');

  const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const selection = document.getSelection();
  const ranges: Range[] = [];
  if (selection) {
    for (let index = 0; index < selection.rangeCount; index += 1) ranges.push(selection.getRangeAt(index).cloneRange());
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.readOnly = true;
  textarea.setAttribute('aria-hidden', 'true');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  textarea.style.width = '1px';
  textarea.style.height = '1px';
  textarea.style.opacity = '0';
  textarea.style.pointerEvents = 'none';
  document.body.appendChild(textarea);

  try {
    textarea.focus({ preventScroll: true });
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    if (!document.execCommand('copy')) throw new Error('浏览器拒绝了复制命令');
  } finally {
    textarea.remove();
    if (selection) {
      selection.removeAllRanges();
      for (const range of ranges) selection.addRange(range);
    }
    activeElement?.focus({ preventScroll: true });
  }
}
